import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, CircularProgress } from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import AlertDialog from 'src/app/common/components/AlertDialog';
import OpmPdReasonFormDialog from 'src/app/common/components/OpmPdReasonFormDialog/OpmPdReasonFormDialog';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import { alertDialog } from 'src/utils/alertDialog';
import { PolicyTaskStatus, policyTaskStatusMap } from '../../../constants/common.constant';
import {
  approveLmBatchFilePolicies,
  fetchLmBatchFile,
  rejectLmBatchFilePolicies,
  sendBackLmBatchFilePolicies,
} from '../../../network/opm-pd-lm';
import AdditionalInformation from './components/AdditionalInformation/AdditionalInformation';
import BachFileActions from './components/BachFileActions/BachFileActions';
import PolicyTable from './components/PolicyTable/PolicyTable';
import {
  PoliciesApprovalRequestPageContext,
  PoliciesApprovalRequestPageContextType,
} from './PoliciesApprovalRequestPage.context';

type PolicyAction = 'sendBack' | 'reject' | 'approve';
type ReasonDialogAction = Extract<PolicyAction, 'sendBack' | 'reject'>;

const PoliciesApprovalRequestPage = () => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { batchId } = useParams<{ batchId: string }>();
  const history = useHistory();

  const [lmBatchFile, setLmBatchFile] = useState<PoliciesApprovalRequestPageContextType['lmBatchFile']>(null);
  const [lmBatchFileLoading, setLmBatchFileLoading] = useState(false);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonDialogAction, setReasonDialogAction] = useState<ReasonDialogAction | null>(null);

  const policies = useMemo(
    () =>
      lmBatchFile?.policies?.map((policy, policyIndex) => {
        // const otherQualifiedAgent = policy.qualifiedAgents?.find((agent) => agent.agentCode === policy.selectedAgent);
        const otherQualifiedAgent = policy.isSelectedOtherAgent ? policy.selectedAgent : '';
        const otherQualifiedAgentName = policy.isSelectedOtherAgent ? policy.selectedAgentName : '';
        const policyTaskStatusLabel =
          policyTaskStatusMap[policy.policyTaskStatus]?.label || policy.policyTaskStatus || '';
        return {
          ...policy,
          _index: policyIndex,
          _otherQualifiedAgent: otherQualifiedAgent,
          _otherQualifiedAgentName: otherQualifiedAgentName,
          _policyTaskStatusLabel: policyTaskStatusLabel,
        };
      }) ?? [],
    [lmBatchFile],
  );

  const validPolicyIds = useMemo(() => {
    return (
      policies
        .filter((policy) => policy.policyTaskStatus === PolicyTaskStatus.AWAITING_LM)
        .map((policy) => policy.id) ?? []
    );
  }, [policies]);

  const fetchLmBatchFileData = async () => {
    setLmBatchFileLoading(true);
    try {
      const data = await fetchLmBatchFile(
        {
          id: batchId,
        },
        dispatch,
      );
      setLmBatchFile(data);
    } catch (error) {
      history.push('/opm-pd/request-approval-to-line-manager/batch-files');
    } finally {
      setLmBatchFileLoading(false);
    }
  };

  const isReasonDialogSubmitting = isSubmitting && !!reasonDialogAction;
  const policyActionTitles: Record<PolicyAction, string> = {
    sendBack: t('opm_pd.sendBack'),
    reject: t('opm_pd.reject'),
    approve: t('opm_pd.approve'),
  };
  const policyActionConfirmMessages: Record<PolicyAction, string> = {
    sendBack: t('opm_pd.confirmSendBackPoliciesContentText'),
    reject: t('opm_pd.confirmRejectPoliciesContentText'),
    approve: t('opm_pd.confirmApprovePoliciesContentText'),
  };
  const policyActionSuccessMessages: Record<PolicyAction, string> = {
    sendBack: t('opm_pd.sendBackPoliciesSuccessfully'),
    reject: t('opm_pd.rejectPoliciesSuccessfully'),
    approve: t('opm_pd.approvePoliciesSuccessfully'),
  };
  const reasonDialogTitle = reasonDialogAction ? policyActionTitles[reasonDialogAction] : '';

  const showSuccessAlert = (content: string) => {
    dispatch(
      appendAlertItem([
        {
          severity: AlertType.SUCCESS,
          title: t('opm_pd.success'),
          content,
        },
      ]),
    );
  };

  const confirmPolicyAction = async (action: PolicyAction) => {
    return alertDialog.fire({
      title: t('opm_pd.confirm'),
      description: policyActionConfirmMessages[action],
    });
  };

  const handleOpenReasonDialog = (action: ReasonDialogAction) => {
    if (!selectedPolicyIds.length || isSubmitting) return;
    setReasonDialogAction(action);
  };

  const handleCloseReasonDialog = () => {
    if (isReasonDialogSubmitting) return;
    setReasonDialogAction(null);
  };

  const approvePolicies = async () => {
    const policyIds = [...selectedPolicyIds];
    if (!policyIds.length || isSubmitting) return;

    const { isConfirmed } = await confirmPolicyAction('approve');
    if (!isConfirmed) return;

    setIsSubmitting(true);
    try {
      await approveLmBatchFilePolicies({ id: batchId, policyIds }, dispatch);
      setSelectedPolicyIds([]);
      await fetchLmBatchFileData();
      showSuccessAlert(policyActionSuccessMessages.approve);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectPolicies = () => handleOpenReasonDialog('reject');

  const sendBackPolicies = () => handleOpenReasonDialog('sendBack');

  const handleSubmitReason = async (reason: string) => {
    if (!reasonDialogAction || isReasonDialogSubmitting) return;

    const action = reasonDialogAction;
    const policyIds = [...selectedPolicyIds];
    if (!policyIds.length) return;

    const { isConfirmed } = await confirmPolicyAction(action);
    if (!isConfirmed) return;

    setIsSubmitting(true);
    try {
      if (action === 'sendBack') {
        await sendBackLmBatchFilePolicies({ id: batchId, policyIds, reason: reason.trim() }, dispatch);
      } else {
        await rejectLmBatchFilePolicies({ id: batchId, policyIds, reason: reason.trim() }, dispatch);
      }

      setSelectedPolicyIds([]);
      await fetchLmBatchFileData();
      showSuccessAlert(policyActionSuccessMessages[action]);
      setReasonDialogAction(null);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchLmBatchFileData();
  }, [batchId]);

  return (
    <PoliciesApprovalRequestPageContext.Provider
      value={{
        lmBatchFile,
        lmBatchFileLoading,
        policies,
        selectedPolicyIds,
        validPolicyIds,
        isSubmitting,
        setSelectedPolicyIds,
        approvePolicies,
        rejectPolicies,
        sendBackPolicies,
      }}
    >
      <AlertDialog />
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="flex-start" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="error"
            component={Link}
            to="/opm-pd/request-approval-to-line-manager/batch-files"
          >
            Back
          </Button>
        </Box>
        {lmBatchFileLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <PolicyTable />
            <AdditionalInformation />
            <BachFileActions />
            <OpmPdReasonFormDialog
              open={!!reasonDialogAction}
              title={reasonDialogTitle}
              isSubmitting={isReasonDialogSubmitting}
              onClose={handleCloseReasonDialog}
              onSubmit={handleSubmitReason}
            />
          </>
        )}
      </Box>
    </PoliciesApprovalRequestPageContext.Provider>
  );
};

export default PoliciesApprovalRequestPage;
